"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useEffect, useId, useRef, useState, useSyncExternalStore, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useForm, type FieldError, type UseFormRegisterReturn } from "react-hook-form";
import { z } from "zod";

import { ReCaptchaProvider } from "@/components/providers/ReCaptchaProvider";
import { RecaptchaNotice } from "@/components/shared/RecaptchaNotice";
import { useRecaptchaToken } from "@/lib/recaptcha-client";

const BG = "bg-[#1a1a1a]";
const ACCENT = "text-[#f16e33]";
const ACCENT_BORDER = "border-[#f16e33]";
const ACCENT_BG = "bg-[#f16e33]";

/** Schema + inferred type — matches React Hook Form’s recommended resolver + Zod pattern. */
export const digitalReportLeadFormSchema = z.object({
  firstName: z.string().trim().min(1, { message: "Enter your first name" }),
  lastName: z.string().trim().min(1, { message: "Enter your last name" }),
  email: z.string().trim().email({ message: "Enter a valid email address" }),
  phone: z.string().trim(),
  businessName: z.string().trim().min(1, { message: "Enter your business name" }),
  website: z.string().trim().min(1, { message: "Enter your website address" }),
});

export type DigitalReportLeadFormValues = z.infer<typeof digitalReportLeadFormSchema>;

type DigitalReportLeadFormProps = {
  /** Prefix for input ids (use in modals or multiple instances on one page). */
  idPrefix?: string;
  className?: string;
  onSubmit?: (values: DigitalReportLeadFormValues) => void | Promise<void>;
  formId?: string;
  /** When set, the header chevron acts as close (drawer/modal). */
  onClose?: () => void;
  /** For focus on open; attach to the chevron close control when `onClose` is set. */
  closeButtonRef?: RefObject<HTMLButtonElement | null>;
  /** Server/API error shown above the fields (e.g. failed email send). */
  submitError?: string | null;
  /** When true, inputs and submit are hidden and {@link successMessage} is shown. */
  submitSuccess?: boolean;
  /** Shown when {@link submitSuccess} is true. */
  successMessage?: string;
};

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="22"
      height="34"
      viewBox="0 0 10 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1.5 1L8.5 8L1.5 15"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SubmitSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <circle
        className="opacity-30"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        className="opacity-90"
      />
    </svg>
  );
}

function UnderlineField({
  id,
  label,
  placeholder,
  type = "text",
  autoComplete,
  required,
  registration,
  error,
}: {
  id: string;
  /** Accessible name; visually hidden. */
  label: string;
  placeholder: string;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  registration: UseFormRegisterReturn;
  error?: FieldError;
}) {
  const errId = `${id}-error`;
  const hasError = Boolean(error);

  return (
    <div className="min-w-0 flex-1">
      <label htmlFor={id} className="sr-only">
        {label}
        {required ? " (required)" : ""}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={hasError}
        aria-describedby={hasError ? errId : undefined}
        className={`w-full border-0 border-b bg-transparent py-1 text-base leading-tight text-white outline-none transition-colors placeholder:tracking-tight placeholder:text-white/40 focus-visible:border-b ${ACCENT_BORDER} ${
          hasError ? "border-b-red-400/90 focus-visible:border-b-red-400" : "border-white/25"
        }`}
        {...registration}
        placeholder={placeholder}
      />
      {hasError ? (
        <p id={errId} className="mt-1 text-xs text-red-400" role="alert">
          {error?.message}
        </p>
      ) : null}
    </div>
  );
}

export function DigitalReportLeadForm({
  idPrefix = "digital-report",
  className = "",
  onSubmit,
  formId,
  onClose,
  closeButtonRef,
  submitError,
  submitSuccess = false,
  successMessage = "Thanks, we've received your details. We're on your digital report and will be in touch as soon as it's ready.",
}: DigitalReportLeadFormProps) {
  const uid = useId();
  const base = `${idPrefix}-${uid.replace(/:/g, "")}`;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DigitalReportLeadFormValues>({
    resolver: zodResolver(digitalReportLeadFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      businessName: "",
      website: "",
    },
  });

  const submit = handleSubmit(async (data) => {
    await onSubmit?.(data);
  });

  return (
    <div className={`${BG} font-sans text-white ${className}`}>
      <div className="mx-auto w-full max-w-[460px] px-6 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12">
        <header>
          <div className="flex flex-col gap-2 text-[#8fa8bf]">
            {onClose ? (
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="flex shrink-0 items-center justify-center self-start rounded-md text-white/75 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white mb-4"
                aria-label="Close"
              >
                <ChevronIcon className="text-current" />
              </button>
            ) : (
              <div className="flex h-12 w-12 items-center justify-center self-start" aria-hidden>
                <ChevronIcon className="text-white/45" />
              </div>
            )}
            <p className="text-sm font-medium tracking-tight uppercase">
              Just how visible is your digital presence
            </p>
          </div>
          <h2
            id={`${base}-heading`}
            className="mt-4 text-3xl leading-[0.9em] text-white tracking-tighter sm:text-4xl lg:text-5xl xl:text-6xl"
          >
     
     
            Get your free{" "}
            <span className="text-[#ff5c00]">digital report</span>
          </h2>
          {!submitSuccess ? (
            <p className="mt-4 text-xs leading-tight text-white/85">
              Flick us your details, and we will work on creating your digital report. However, they don&apos;t happen
              overnight, so hold on tight and we will get it to you as soon as possible. In the meantime, if you have any
              questions, please don&apos;t hesitate to call us on{" "}
              <a href="tel:+6421471358" className="font-medium text-[#ff5c00] underline-offset-2 hover:underline">
                021 471 358
              </a>
              .
            </p>
          ) : null}
        </header>

        {submitSuccess ? (
          <div
            className="mt-10 rounded-lg border border-white/15 bg-white/5 px-5 py-6 sm:px-6"
            role="status"
            aria-live="polite"
          >
            <p className="text-base leading-relaxed text-white/90">{successMessage}</p>
          </div>
        ) : (
          <>
            {submitError ? (
              <p
                className="mt-6 rounded-md border border-red-400/40 bg-red-950/40 px-3 py-2 text-sm text-red-300"
                role="alert"
              >
                {submitError}
              </p>
            ) : null}

            <form id={formId} className="mt-10 space-y-8" onSubmit={submit} noValidate>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-x-8">
                <UnderlineField
                  id={`${base}-firstName`}
                  label="First name"
                  placeholder="First name*"
                  autoComplete="given-name"
                  required
                  registration={register("firstName")}
                  error={errors.firstName}
                />
                <UnderlineField
                  id={`${base}-lastName`}
                  label="Last name"
                  placeholder="Last name*"
                  autoComplete="family-name"
                  required
                  registration={register("lastName")}
                  error={errors.lastName}
                />
              </div>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-x-8">
                <UnderlineField
                  id={`${base}-email`}
                  label="Email"
                  placeholder="Email*"
                  type="email"
                  autoComplete="email"
                  required
                  registration={register("email")}
                  error={errors.email}
                />
                <UnderlineField
                  id={`${base}-phone`}
                  label="Phone"
                  placeholder="Phone"
                  type="tel"
                  autoComplete="tel"
                  registration={register("phone")}
                  error={errors.phone}
                />
              </div>
              <UnderlineField
                id={`${base}-businessName`}
                label="Business name"
                placeholder="Business name*"
                autoComplete="organization"
                required
                registration={register("businessName")}
                error={errors.businessName}
              />
              <UnderlineField
                id={`${base}-website`}
                label="Website address"
                placeholder="Website address*"
                type="text"
                autoComplete="url"
                required
                registration={register("website")}
                error={errors.website}
              />

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  className={`inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:cursor-not-allowed disabled:opacity-60 ${ACCENT_BG} cursor-pointer ${isSubmitting ? "gap-2" : ""}`}
                >
                  {isSubmitting ? (
                    <SubmitSpinner className="shrink-0 animate-spin text-white" />
                  ) : null}
                  Send me my report
                </button>
              </div>
            </form>

            <p className="mt-8 text-[11px] leading-tight text-white/80 md:text-xs md:leading-snug">
              By providing us with your information, you are consenting to the collection and use of your information in
              accordance with our{" "}
              <Link href="/terms" className={`underline underline-offset-2 hover:opacity-90 ${ACCENT}`}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link href="/privacy" className={`underline underline-offset-2 hover:opacity-90 ${ACCENT}`}>
                Privacy Policy
              </Link>
              .
            </p>
            <RecaptchaNotice
              className="mt-3 text-[11px] leading-tight text-white/80 md:text-xs md:leading-snug"
              linkClassName={`underline underline-offset-2 hover:opacity-90 ${ACCENT}`}
            />
          </>
        )}
      </div>
    </div>
  );
}

export type DigitalReportLeadSubmitHandler = (
  values: DigitalReportLeadFormValues,
  recaptchaToken: string,
) => Promise<void>;

function DigitalReportLeadFormWithRecaptcha({
  onLeadSubmit,
  onRecaptchaError,
  ...formProps
}: Omit<DigitalReportLeadFormProps, "onSubmit"> & {
  onLeadSubmit: DigitalReportLeadSubmitHandler;
  onRecaptchaError: (message: string) => void;
}) {
  const { getToken } = useRecaptchaToken();
  const onSubmit = async (values: DigitalReportLeadFormValues) => {
    const recaptcha = await getToken("digital_report_submit");
    if (!recaptcha.ok) {
      onRecaptchaError(recaptcha.error);
      return;
    }
    await onLeadSubmit(values, recaptcha.token);
  };
  return <DigitalReportLeadForm {...formProps} onSubmit={onSubmit} />;
}

export type DigitalReportLeadFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit?: (values: DigitalReportLeadFormValues) => void | Promise<void>;
  /** Unique id prefix when multiple modals exist. */
  idPrefix?: string;
  submitError?: string | null;
  submitSuccess?: boolean;
  successMessage?: string;
};

export type DigitalReportLeadFormDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Used when {@link onLeadSubmit} is not set. */
  onSubmit?: (values: DigitalReportLeadFormValues) => void | Promise<void>;
  /**
   * When set, reCAPTCHA loads only while the drawer is open and mounted; the token is passed here.
   * Prefer this for production so the script is not loaded site-wide.
   */
  onLeadSubmit?: DigitalReportLeadSubmitHandler;
  onRecaptchaError?: (message: string) => void;
  idPrefix?: string;
  submitError?: string | null;
  submitSuccess?: boolean;
  successMessage?: string;
};

/**
 * Full-viewport overlay + panel that slides in from the right; height follows content up to the viewport max.
 */
function useDocumentBody(): HTMLElement | null {
  return useSyncExternalStore(
    () => () => {},
    () => document.body,
    () => null,
  );
}

export function DigitalReportLeadFormDrawer({
  open,
  onOpenChange,
  onSubmit,
  onLeadSubmit,
  onRecaptchaError,
  idPrefix = "digital-report-drawer",
  submitError,
  submitSuccess,
  successMessage,
}: DigitalReportLeadFormDrawerProps) {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const portalTarget = useDocumentBody();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const DRAWER_TRANSITION_MS = 320;

  // Drive enter/exit transforms: mount before opening animation; unmount after close transition.
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- drawer mount + slide animation synced to `open` */
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntered(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open]);

  // If transitionend never fires (browser quirk / layout), still unmount so the invisible overlay cannot block the page.
  useEffect(() => {
    if (open || !mounted) return;
    const t = window.setTimeout(() => setMounted(false), DRAWER_TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [open, mounted]);

  useEffect(() => {
    if (!mounted || !entered) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mounted, entered, onOpenChange]);

  useEffect(() => {
    if (!mounted || !entered) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted, entered]);

  useEffect(() => {
    if (entered) closeBtnRef.current?.focus();
  }, [entered]);

  const handleBackdropPointerDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onOpenChange(false);
  };

  const handlePanelTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== "transform") return;
    if (!open) setMounted(false);
  };

  /** Only while `open` is true — when parent closes, unblock clicks immediately (avoids invisible full-screen hit target). */
  const overlayInteractive = open;

  if (!portalTarget || !mounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-100 flex items-center justify-end ${overlayInteractive ? "pointer-events-auto" : "pointer-events-none"}`}
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close form"
        className={`absolute inset-0 bg-black/75 transition-opacity duration-300 ease-out ${
          entered ? "opacity-100" : "opacity-0"
        }`}
        tabIndex={-1}
        onMouseDown={handleBackdropPointerDown}
      />
      <div
        className={`relative z-10 flex h-auto max-h-[calc(100dvh-1rem)] w-full max-w-[min(100%,480px)] flex-col overflow-y-auto overscroll-contain rounded-l-lg shadow-2xl transition-transform duration-300 ease-out ${
          entered ? "translate-x-0" : "translate-x-full"
        } ${BG}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${idPrefix}-dialog-title`}
        onMouseDown={(e) => e.stopPropagation()}
        onTransitionEnd={handlePanelTransitionEnd}
      >
        <span id={`${idPrefix}-dialog-title`} className="sr-only">
          Get your free digital report
        </span>
        {onLeadSubmit && open && mounted ? (
          <ReCaptchaProvider>
            <DigitalReportLeadFormWithRecaptcha
              idPrefix={idPrefix}
              closeButtonRef={closeBtnRef}
              onClose={() => onOpenChange(false)}
              submitError={submitError}
              submitSuccess={submitSuccess}
              successMessage={successMessage}
              onLeadSubmit={onLeadSubmit}
              onRecaptchaError={onRecaptchaError ?? (() => {})}
            />
          </ReCaptchaProvider>
        ) : (
          <DigitalReportLeadForm
            idPrefix={idPrefix}
            closeButtonRef={closeBtnRef}
            onClose={() => onOpenChange(false)}
            submitError={submitError}
            submitSuccess={submitSuccess}
            successMessage={successMessage}
            onSubmit={
              onLeadSubmit
                ? async () => {}
                : async (values) => {
                    await onSubmit?.(values);
                  }
            }
          />
        )}
      </div>
    </div>,
    portalTarget,
  );
}

export function DigitalReportLeadFormModal({
  open,
  onOpenChange,
  onSubmit,
  idPrefix = "digital-report-modal",
  submitError,
  submitSuccess,
  successMessage,
}: DigitalReportLeadFormModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const onClose = () => {
      onOpenChange(false);
    };
    el.addEventListener("close", onClose);
    return () => el.removeEventListener("close", onClose);
  }, [onOpenChange]);

  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className="m-0 max-h-dvh max-w-none border-0 bg-transparent p-0 shadow-none outline-none backdrop:bg-black/75"
      aria-labelledby={`${idPrefix}-dialog-title`}
    >
      <div
        className="fixed inset-0 flex items-start justify-center overflow-y-auto bg-black/75 p-4 sm:p-6"
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onOpenChange(false);
        }}
      >
        <div
          className={`relative my-4 h-auto max-h-[calc(100dvh-2rem)] w-full max-w-[480px] overflow-y-auto overscroll-contain rounded-lg shadow-2xl sm:my-8 ${BG}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <span id={`${idPrefix}-dialog-title`} className="sr-only">
            Get your free digital report
          </span>
          <DigitalReportLeadForm
            idPrefix={idPrefix}
            closeButtonRef={closeBtnRef}
            onClose={() => onOpenChange(false)}
            submitError={submitError}
            submitSuccess={submitSuccess}
            successMessage={successMessage}
            onSubmit={async (values) => {
              await onSubmit?.(values);
            }}
          />
        </div>
      </div>
    </dialog>
  );
}
